import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useProfile, useUpdateProfile } from '@/lib/hooks'
import { profileUpdateSchema, type ProfileUpdateData } from '@/lib/validation'
import { useEffect } from 'react'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function ProfileForm() {
  const { t } = useTranslation()
  const { data: profile, isLoading: profileLoading, error: profileError } = useProfile()
  const updateProfileMutation = useUpdateProfile()
  const canUpdateProfile = useHasPermission('profile.update')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileUpdateData>({
    resolver: zodResolver(profileUpdateSchema),
  })

  // Populate form with current profile data
  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.firstName,
        last_name: profile.lastName,
        display_name: profile.displayName || '',
        language: profile.language,
        timezone: profile.timezone,
      })
    }
  }, [profile, reset])

  const handleFormSubmit = async (data: ProfileUpdateData) => {
    try {
      await updateProfileMutation.mutateAsync(data)
      // TODO: Show success message
    } catch (error) {
      console.error('Failed to update profile:', error)
      // TODO: Show error message
    }
  }

  if (profileLoading) {
    return <div className="text-center py-8">Laden...</div>
  }

  if (profileError) {
    console.error('Profile fetch error:', profileError)
    return (
      <div className="text-center py-8">
        <p className="text-destructive">Fout bij het laden van profielgegevens</p>
        <p className="text-sm text-muted-foreground mt-2">
          Probeer de pagina te vernieuwen of neem contact op met de beheerder.
        </p>
      </div>
    )
  }

  if (!canUpdateProfile) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          {t('profile.noUpdatePermission', 'U heeft geen toestemming om uw profiel bij te werken.')}
        </p>
      </div>
    )
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label htmlFor="first_name" variant="required">
              {t('profile.firstName', 'Voornaam')}
            </Label>
            <Input
              id="first_name"
              {...register('first_name')}
              placeholder="Uw voornaam"
              error={!!errors.first_name}
              aria-invalid={!!errors.first_name}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {errors.first_name.message}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="last_name" variant="required">
              {t('profile.lastName', 'Achternaam')}
            </Label>
            <Input
              id="last_name"
              {...register('last_name')}
              placeholder="Uw achternaam"
              error={!!errors.last_name}
              aria-invalid={!!errors.last_name}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Label htmlFor="display_name">{t('profile.displayName', 'Weergavenaam')}</Label>
          <Input
            id="display_name"
            {...register('display_name')}
            placeholder="Optionele weergavenaam"
            error={!!errors.display_name}
            aria-invalid={!!errors.display_name}
          />
          {errors.display_name && (
            <p className="text-sm text-destructive mt-1" role="alert">
              {errors.display_name.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label htmlFor="language" variant="required">
              {t('profile.language', 'Taal')}
            </Label>
            <select
              id="language"
              {...register('language')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.language}
            >
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
            {errors.language && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {errors.language.message}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="timezone" variant="required">
              {t('profile.timezone', 'Tijdzone')}
            </Label>
            <select
              id="timezone"
              {...register('timezone')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.timezone}
            >
              <option value="Europe/Amsterdam">Europe/Amsterdam</option>
              <option value="UTC">UTC</option>
            </select>
            {errors.timezone && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {errors.timezone.message}
              </p>
            )}
          </div>
        </div>

        {/* Read-only email field */}
        <div className="space-y-3">
          <Label htmlFor="email">{t('profile.email', 'E-mailadres')}</Label>
          <Input id="email" value={profile?.email || ''} disabled className="bg-muted" />
          <p className="text-sm text-muted-foreground">
            {t('profile.emailNote', 'E-mailadres kan niet worden gewijzigd')}
          </p>
        </div>

        <Button
          type="submit"
          variant="gradient"
          className="w-full"
          loading={updateProfileMutation.isPending}
          disabled={updateProfileMutation.isPending}
        >
          {updateProfileMutation.isPending
            ? t('algemeen.laden', 'Laden...')
            : t('profile.saveChanges', 'Wijzigingen opslaan')}
        </Button>
      </form>
    </div>
  )
}
